import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Flex, Message, Modal } from '@workwhile/ui'
import { useIsTippingEnabled } from 'hooks/useIsTippingEnabled'
import { formatCurrency } from 'lib/string'
import { useSendTipsMutation } from 'queries/worker'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { sendTipsFormSchema } from './schema'

import { SelectTipShift } from './components/SelectTipShift'
import { ShiftTipHistory } from './components/ShiftTipHistory'
import { TipAmountInput } from './components/TipAmountInput'

type Props = {
  isOpen: boolean
  onClose: () => void
  workerId: number
  workerName: string
}

export const SendTipModal = ({
  isOpen,
  onClose,
  workerId,
  workerName,
}: Props) => {
  const [errorMessage, setError] = useState<string | null>(null)
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      amount: null,
      workId: null,
    },
    resolver: zodResolver(sendTipsFormSchema),
  })

  const { mutate: sendTips, isPending: isSendTipPending } = useSendTipsMutation(
    { workerId },
    {
      onError: (error) => setError(error.message),
    }
  )

  const workId = form.watch('workId')
  const amount = form.watch('amount')
  const error = errorMessage || form.formState.errors.root?.message
  const isDisabled = !amount || !workId

  const onSubmit = form.handleSubmit((formData) => {
    setError(null)
    form.setValue('amount', null)
    sendTips({ tips: [{ workId: formData.workId, amount: formData.amount }] })
  })

  useEffect(() => {
    if (isOpen && !workerId) {
      setError('Worker not found. Please contact support.')
    }
  }, [isOpen, workerId])

  return (
    <Modal
      key={`SendTipModal-${workerId}-${isOpen}`}
      onClose={onClose}
      open={isOpen}
      title={`Send ${workerName} a Tip`}
      customCta={
        <Button
          disabled={isDisabled}
          loading={isSendTipPending}
          type="submit"
          form="send-tip-form"
        >
          Send {!isDisabled ? formatCurrency(amount) : 'Tip'}
        </Button>
      }
    >
      <FormProvider {...form}>
        <form id="send-tip-form" onSubmit={onSubmit}>
          <Flex
            flexDirection="column"
            gap={2}
            minHeight="200px"
            overflowY="scroll"
          >
            <TipAmountInput />
            <SelectTipShift workerId={workerId} required isSearchable />

            <Flex flexDirection="column" gap={4}>
              <ShiftTipHistory workerId={workerId} workId={workId} />
              {error && <Message variant="error">{error}</Message>}
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  )
}
